export type UserProfileLike = {
  _id?: string;
  role?: string;
  email?: string;
  meaLogonName?: string;
  tenantAdmin?: boolean;
  roles?: string[];
  data?: UserProfileLike;
  [key: string]: unknown;
} | null | undefined;

/** Unwrap profile payloads shaped as `{ data: { role, _id, ... } }`. */
export function normalizeUserProfile<T extends UserProfileLike>(
  profile: T
): T {
  if (!profile || typeof profile !== "object") {
    return profile;
  }

  const inner = profile.data;
  if (
    inner &&
    typeof inner === "object" &&
    !profile.role &&
    (inner.role || inner._id || inner.email || inner.meaLogonName)
  ) {
    const { data: _nested, ...rest } = profile;
    return { ...rest, ...inner } as T;
  }

  return profile;
}

/** Legacy profile.role values that should map to current workflow symbols. */
const WORKFLOW_ROLE_ALIASES: Record<string, string> = {
  __ROLE_WF_CEO: "__WF_DATA_CEO",
  __ROLE_WF_CHECKER: "__WF_DATA_CHECKER",
  __ROLE_WF_SUBMIT: "__WF_DATA_INPUT",
  __ROLE_WF_APPROVE: "__WF_DATA_APPROVE",
  __ROLE_WF_FINAL_APPROVE: "__WF_DATA_FINAL_APPROVE",
};

/** When profile.role is comma-separated, pick the most specific workflow role. */
const WORKFLOW_ROLE_PRIORITY = [
  "__WF_DATA_FINAL_APPROVE",
  "__WF_DATA_APPROVE",
  "__WF_DATA_CHECKER",
  "__WF_DATA_INPUT",
  "__WF_DATA_CEO",
] as const;

export function normalizeWorkflowRoleSymbol(symbol: string): string {
  const trimmed = symbol.trim();
  return WORKFLOW_ROLE_ALIASES[trimmed] || trimmed;
}

export function getUserWorkflowRoles(profile: UserProfileLike): string[] {
  const normalized = normalizeUserProfile(profile);
  const raw = normalized?.role?.trim();
  if (!raw) return [];

  return [
    ...new Set(
      raw
        .split(",")
        .map((part) => normalizeWorkflowRoleSymbol(part))
        .filter(Boolean)
    ),
  ];
}

export function getUserProfileRole(
  profile: UserProfileLike
): string | null {
  const roles = getUserWorkflowRoles(profile);
  if (roles.length === 0) return null;
  if (roles.length === 1) return roles[0];

  for (const symbol of WORKFLOW_ROLE_PRIORITY) {
    if (roles.includes(symbol)) return symbol;
  }

  return roles[0];
}

export function getUserProfileId(
  profile: UserProfileLike
): string {
  const normalized = normalizeUserProfile(profile);
  return String(normalized?._id || "");
}

export function getUserProfileEmail(
  profile: UserProfileLike
): string {
  const normalized = normalizeUserProfile(profile);
  return String(normalized?.email || "");
}
